import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import {
  ErrorMessage,
  Card,
  Badge,
  PageHeader,
  NoOrganizationMessage,
  Table,
  DataTable,
  Button,
  Select,
  DateRangePicker,
  SearchInput,
  Pagination,
  formatDateTime,
} from '../components';
import { auditService, type AuditEntry, type AuditAction } from '../api/services/AuditService';

const ACTION_COLORS: Record<AuditAction, string> = {
  INSERT: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  RESTORE: 'bg-yellow-100 text-yellow-800',
};

const ACTION_LABELS: Record<AuditAction, string> = {
  INSERT: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  RESTORE: 'Restored',
};

const TABLE_LABELS: Record<string, string> = {
  transactions: 'Tapahtumat',
  journal_entries: 'Kirjaukset',
  organizations: 'Organisaatiot',
  users: 'Käyttäjät',
  invoices: 'Laskut',
  files: 'Tiedostot',
};

interface Filters {
  tableName: string;
  action: AuditAction | '';
  changedBy: string;
  startDate: string;
  endDate: string;
}

export default function AuditTrailPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<Filters>({
    tableName: '',
    action: '',
    changedBy: '',
    startDate: '',
    endDate: '',
  });
  const limit = 25;

  const fetchEntries = useCallback(async () => {
    if (!activeOrg) return;

    setLoading(true);
    setError('');

    try {
      const offset = (currentPage - 1) * limit;
      const response = await auditService.list({
        organizationId: activeOrg.id,
        tableName: filters.tableName || undefined,
        action: filters.action || undefined,
        changedBy: filters.changedBy || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        limit,
        offset,
      });

      setEntries(response.entries);
      setTotalCount(response.pagination.count);
    } catch (err) {
      setError('Muutoshistorian lataus epäonnistui');
      console.error('Failed to fetch audit entries:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOrg, filters, currentPage]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleExportPDF = async () => {
    if (!activeOrg) return;
    
    try {
      const blob = await auditService.exportToPDF({
        organizationId: activeOrg.id,
        tableName: filters.tableName || undefined,
        action: filters.action || undefined,
        changedBy: filters.changedBy || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('PDF-vienti epäonnistui');
      console.error('Failed to export PDF:', err);
    }
  };

  const handleExportExcel = async () => {
    if (!activeOrg) return;
    
    try {
      const blob = await auditService.exportToExcel({
        organizationId: activeOrg.id,
        tableName: filters.tableName || undefined,
        action: filters.action || undefined,
        changedBy: filters.changedBy || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Excel-vienti epäonnistui');
      console.error('Failed to export Excel:', err);
    }
  };

  const renderDiff = (oldData: Record<string, unknown> | null, newData: Record<string, unknown> | null) => {
    if (!oldData && !newData) return <p className="text-gray-400">Ei dataa</p>;
    if (!oldData) return <p className="text-green-600">Uusi tietue luotu</p>;
    if (!newData) return <p className="text-red-600">Tietue poistettu</p>;
    
    const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
    const changes: Array<{ key: string; old: unknown; new: unknown }> = [];
    
    allKeys.forEach(key => {
      if (oldData[key] !== newData[key]) {
        changes.push({ key, old: oldData[key], new: newData[key] });
      }
    });
    
    if (changes.length === 0) return <p className="text-gray-400">Ei muutoksia</p>;
    
    return (
      <div className="space-y-2">
        {changes.map(({ key, old, new: newVal }) => (
          <div key={key} className="grid grid-cols-3 gap-4 text-sm">
            <div className="font-medium text-gray-600">{key}</div>
            <div className="text-red-600 line-through">{String(old ?? '-')}</div>
            <div className="text-green-600">{String(newVal ?? '-')}</div>
          </div>
        ))}
      </div>
    );
  };

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Muutoshistoria — Audit Trail" />
      
      {/* Filters */}
      <Card title="Suodattimet">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            label="Taulu"
            value={filters.tableName}
            onChange={(e) => setFilters(f => ({ ...f, tableName: e.target.value }))}
            options={[
              { value: '', label: 'Kaikki taulut' },
              { value: 'transactions', label: 'Tapahtumat' },
              { value: 'journal_entries', label: 'Kirjaukset' },
              { value: 'invoices', label: 'Laskut' },
              { value: 'users', label: 'Käyttäjät' },
            ]}
          />
          <Select
            label="Toiminto"
            value={filters.action}
            onChange={(e) => setFilters(f => ({ ...f, action: e.target.value as AuditAction | '' }))}
            options={[
              { value: '', label: 'Kaikki toiminnot' },
              { value: 'INSERT', label: t('audit.actionInsert') },
              { value: 'UPDATE', label: 'Päivitetty' },
              { value: 'DELETE', label: 'Poistettu' },
              { value: 'RESTORE', label: 'Palautettu' },
            ]}
          />
          <SearchInput
            label="Käyttäjä"
            value={filters.changedBy}
            onChange={(e) => setFilters(f => ({ ...f, changedBy: e.target.value }))}
            placeholder={t('audit.searchByUser')}
          />
          <DateRangePicker
            startLabel="Alkaen"
            endLabel="Päättyen"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={(v) => setFilters(f => ({ ...f, startDate: v }))}
            onEndDateChange={(v) => setFilters(f => ({ ...f, endDate: v }))}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="primary" onClick={fetchEntries}>
            🔄 Päivitä
          </Button>
          <Button variant="secondary" onClick={handleExportPDF}>
            📄 PDF
          </Button>
          <Button variant="secondary" onClick={handleExportExcel}>
            📊 Excel
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && <ErrorMessage message={error} />}

      {/* Audit Trail Table */}
      <Card title="Muutoshistoria" subtitle={`Yhteensä ${totalCount} muutosta`}>
        <DataTable>
          <Table
            columns={[
              {
                key: 'changedAt',
                header: 'Aika',
                width: '180px',
                render: (entry: AuditEntry) => formatDateTime(entry.changedAt),
              },
              {
                key: 'action',
                header: 'Toiminto',
                width: '120px',
                render: (entry: AuditEntry) => (
                  <Badge className={ACTION_COLORS[entry.action]}>
                    {ACTION_LABELS[entry.action]}
                  </Badge>
                ),
              },
              {
                key: 'tableName',
                header: 'Taulu',
                width: '150px',
                render: (entry: AuditEntry) => TABLE_LABELS[entry.tableName] || entry.tableName,
              },
              {
                key: 'recordId',
                header: 'Tietue',
                width: '200px',
                render: (entry: AuditEntry) => (
                  <span className="font-mono text-xs">{entry.recordId.slice(0, 8)}...</span>
                ),
              },
              {
                key: 'changedBy',
                header: 'Käyttäjä',
                render: (entry: AuditEntry) => entry.changedBy || 'Järjestelmä',
              },
              {
                key: 'actions',
                header: '',
                width: '100px',
                align: 'right',
                render: (entry: AuditEntry) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedEntry(entry)}
                  >
                    🔍 Näytä
                  </Button>
                ),
              },
            ]}
            data={entries}
            keyExtractor={(entry) => entry.id}
            loading={loading}
            emptyMessage="Ei muutoksia valituilla suodattimilla"
          />
        </DataTable>
        
        {/* Pagination */}
        {totalCount > limit && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / limit)}
              onPageChange={setCurrentPage}
              totalItems={totalCount}
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Muutoksen tiedot</h3>
                <button
                  onClick={() => setSelectedEntry(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Aika:</span>{' '}
                  {formatDateTime(selectedEntry.changedAt)}
                </div>
                <div>
                  <span className="text-gray-500">Toiminto:</span>{' '}
                  <Badge className={ACTION_COLORS[selectedEntry.action]}>
                    {ACTION_LABELS[selectedEntry.action]}
                  </Badge>
                </div>
                <div>
                  <span className="text-gray-500">Taulu:</span>{' '}
                  {TABLE_LABELS[selectedEntry.tableName] || selectedEntry.tableName}
                </div>
                <div>
                  <span className="text-gray-500">Käyttäjä:</span>{' '}
                  {selectedEntry.changedBy || 'Järjestelmä'}
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Tietue ID:</span>{' '}
                  <span className="font-mono">{selectedEntry.recordId}</span>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-semibold mb-3">Muutokset</h4>
                {renderDiff(selectedEntry.oldData, selectedEntry.newData)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
